// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { useCallback, useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Switch } from 'react-native';

import { Text, View } from '@/components/Themed';
import { getCourseTraceConsent, setCourseTraceConsent } from '@/features/course-trace/consent';
import { useI18n } from '@/i18n';

/**
 * Consent for course-mapping GPS capture.
 *
 * Android only. Capture needs a foreground service that web cannot hold and that iOS has
 * not been set up for, so on any other platform this screen says so rather than offering
 * a switch that would do nothing.
 *
 * On by default — flipping the switch off stores an explicit refusal. The copy states
 * plainly what is recorded, why, how long it is kept and what it costs in battery — the
 * battery line is there because it is the thing players will actually notice.
 */
export default function CourseMappingConsentScreen() {
  const { t } = useI18n();
  const supported = Platform.OS === 'android';

  // Mirrors the storage default so the switch does not flick off-then-on while loading.
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void getCourseTraceConsent().then((granted) => {
      if (cancelled) return;
      setEnabled(granted);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onToggle = useCallback(
    (next: boolean) => {
      // Optimistic, then reverted if the write fails: a switch that does not move feels
      // broken, and a switch that lies about consent is worse.
      setEnabled(next);
      void setCourseTraceConsent(next).catch(() => setEnabled(!next));
    },
    []
  );

  const SECTIONS = [
    ['courseMapping.whatH', 'courseMapping.whatP'],
    ['courseMapping.whyH', 'courseMapping.whyP'],
    ['courseMapping.batteryH', 'courseMapping.batteryP'],
    ['courseMapping.retentionH', 'courseMapping.retentionP'],
    ['courseMapping.controlH', 'courseMapping.controlP'],
  ] as const;

  return (
    <>
      <Stack.Screen options={{ title: t('courseMapping.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('courseMapping.title')}</Text>
        <Text style={styles.intro}>{t('courseMapping.intro')}</Text>

        {supported ? (
          <View style={styles.toggleRow}>
            <Text style={styles.toggleLabel}>{t('courseMapping.toggleLabel')}</Text>
            <Switch
              testID="course-mapping-consent-switch"
              accessibilityLabel={t('courseMapping.toggleLabel')}
              value={enabled}
              disabled={!loaded}
              onValueChange={onToggle}
            />
          </View>
        ) : (
          <View style={styles.section}>
            <Text style={styles.paragraph} testID="course-mapping-unsupported">
              {t('courseMapping.unsupported')}
            </Text>
          </View>
        )}

        {SECTIONS.map(([heading, body]) => (
          <View key={heading} style={styles.section}>
            <Text style={styles.heading}>{t(heading)}</Text>
            <Text style={styles.paragraph}>{t(body)}</Text>
          </View>
        ))}
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 20,
    paddingBottom: 48,
    maxWidth: 720,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
  },
  intro: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 8,
    marginBottom: 20,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: '600',
    flexShrink: 1,
    paddingRight: 12,
  },
  section: {
    marginBottom: 18,
  },
  heading: {
    fontSize: 17,
    fontWeight: '600',
    marginBottom: 6,
  },
  paragraph: {
    fontSize: 16,
    lineHeight: 24,
  },
});
